import css from './ForgotPasswordForm.module.css';

import TextInput from '../../Common/TextInput/TextInput';
import Validators from '../../../utility/validators';
import {
  LoginPageAction,
  FormField,
  FormStatus,
  ForgotPasswordResult,
  ErrorCode,
} from '../../../types/appTypes';
import { useForm } from '../../../hooks/useForm';
import DataForm from '../../Common/DataForm/DataForm';
import { useContext, useState } from 'react';
import { UserContext } from '../../../context/userContext';

type ForgotPasswordFormProps = {
  onCancel: (action: LoginPageAction) => void;
};

type ForgotPasswordFormFields = {
  email: FormField<string>;
};

const ForgotPasswordForm = ({ onCancel }: ForgotPasswordFormProps) => {
  const { forgotPassword } = useContext(UserContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { fields, formState, setFields, setFormState } =
    useForm<ForgotPasswordFormFields>({
      initialFields: {
        email: { value: '', valid: false },
      },
    });

  const formIsValid = fields.email.valid;

  const formRequiredInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    const valid = Validators.requiredEmail(value);
    setFields((pv) => {
      return { ...pv, [name]: { value: value, valid: valid } };
    });
  };

  const cancelHandler = (event: React.MouseEvent) => {
    onCancel(LoginPageAction.Login);
  };

  const formSubmitHandler = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formIsValid) {
      return;
    }

    setFormState({ status: FormStatus.Submitting });
    setIsSubmitting(true);
    
    try {
      // Make sure to lowercase the email for consistency
      // const result = await forgotPassword!(fields.email.value.toLowerCase());
      const result = await forgotPassword!(fields.email.value);
      
      switch (result) {
        case ForgotPasswordResult.Success:
          setFormState({ status: FormStatus.Success });
          break;
        case ForgotPasswordResult.NotFound:
          setFormState({
            status: FormStatus.NotFound,
            error: {
              code: ErrorCode.notFound,
              message: 'A user was not found with that email address. Please verify your email or register for an account.'
            },
          });
          break;
        default:
          setFormState({
            status: FormStatus.Fail,
            error: {
              code: ErrorCode.unknown,
              message: 'An error occurred processing your request. Please try again later.'
            },
          });
          break;
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setFormState({
        status: FormStatus.Error,
        error: {
          code: ErrorCode.unknown,
          message: 'An unexpected error occurred. Please try again later.'
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DataForm
      title='RESET PASSWORD'
      successMessage='An email has been sent with a link to reset your password.'
      submitErrorMessage={formState.error?.message}
      secondaryContainerClass={css.container}
      status={formState.status}
      onSubmit={formSubmitHandler}
    >
      <div className={css.instructions}>
        <p>
          Enter your email below and we will send you a link to reset your
          password
        </p>
      </div>
      <div className={css.formGroup}>
        <TextInput
          placeholder='Email'
          type='email'
          name='email'
          value={fields.email.value}
          valid={fields.email.valid}
          onChange={formRequiredInputChangeHandler}
          disabled={isSubmitting}
        />
      </div>
      <div className={css.actions}>
        <button 
          type='submit' 
          disabled={!formIsValid || isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Reset Link'}
        </button>
        <button 
          type='button' 
          onClick={cancelHandler}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </DataForm>
  );
};

export default ForgotPasswordForm;